// Every settings page: a header (its name, what it does in a line, and its
// main actions at the right), its tabs when it has them, then its body. A
// page's body says what its actions and status are with <PageActions> and
// <PageStatus>, which render into the header wherever they sit in the body,
// so a page's state stays where it lives. Outside a page (a test, a dialog)
// they render in place.
import { createContext, useContext, useState } from "react";
import { createPortal } from "react-dom";
import Tabs from "../ui/Tabs.jsx";

const Slots = createContext(null);

export const PAGE_TITLE = "m-0 min-w-0 text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-[color:var(--ink)] [overflow-wrap:anywhere]";
export const PAGE_DESCRIPTION = "m-0 max-w-[72ch] text-[13.5px] leading-[1.45] text-[color:var(--muted)]";
// A part of a page with its own name (two settings that share a page).
export const PAGE_PART = "m-0 text-[15px] font-medium leading-[1.3] text-[color:var(--ink)]";

function Slot({ name, inline, children }) {
    const slots = useContext(Slots);
    if (!slots) return inline(children);
    const node = slots[name];
    return node ? createPortal(children, node) : null;
}

export function PageActions({ children }) {
    return (
        <Slot name="actions" inline={(c) => <div className="flex flex-wrap items-center gap-[8px] pb-[12px]">{c}</div>}>
            {children}
        </Slot>
    );
}

export function PageStatus({ children }) {
    return <Slot name="status" inline={(c) => c}>{children}</Slot>;
}

// `tabs` is [{ id, label }] with `tab` the open one; `lead` sits before the
// title (a service's logo).
export default function Page({ title, description = null, lead = null, tabs = null, tab = null, onTab = null, tabAttrs = null, children, ...attrs }) {
    const [actions, setActions] = useState(null);
    const [status, setStatus] = useState(null);
    return (
        <Slots.Provider value={{ actions, status }}>
            <section aria-label={title} data-settings-page="" className="flex min-w-0 flex-1 flex-col" {...attrs}>
                <header className="flex flex-wrap items-start justify-between gap-x-[24px] gap-y-[12px] pb-[18px]">
                    <div className="grid min-w-0 flex-1 gap-[6px]">
                        <div className="flex min-h-[30px] flex-wrap items-center gap-x-[12px] gap-y-[6px]">
                            {lead}
                            <h2 className={PAGE_TITLE}>{title}</h2>
                            <span ref={setStatus} className="contents" data-page-status="" />
                        </div>
                        {description ? <p className={PAGE_DESCRIPTION}>{description}</p> : null}
                    </div>
                    <div ref={setActions} className="flex flex-wrap items-center gap-[8px] empty:hidden" data-page-actions="" />
                </header>
                {tabs && tabs.length > 1 ? (
                    <Tabs items={tabs} value={tab} onChange={onTab} label={`${title} tabs`} className="mb-[18px] self-start" itemAttrs={tabAttrs} />
                ) : null}
                <div className="flex min-w-0 flex-1 flex-col">{children}</div>
            </section>
        </Slots.Provider>
    );
}
